import { Link } from "react-router-dom"
import { Menu, X, ChevronDown, ShoppingBag } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useState, useRef, useEffect, lazy, Suspense } from "react"
import CartDrawer from "@/components/CartDrawer"
import AnnouncementBar from "@/components/AnnouncementBar"
import useScrollPosition from "@/hooks/useScrollPosition"
import logo from "@/assets/logo-new.webp"
import { cn } from "@/lib/utils"

const MobileMenu = lazy(() => import("@/components/MobileMenu"))

const shopLinks = [
  { href: "/trgovina", label: "Vse ploščice", description: "Posamezne ploščice in kompleti" },
  { href: "/trgovina?tab=kompleti", label: "Kompleti", description: "Pripravljeni seti za takojšnje odkrivanje" },
]

const navLinks = [
  { href: "/galerija", label: "Galerija" },
  { href: "/nase-ploscice", label: "Naše ploščice" },
  { href: "/o-nas", label: "O nas" },
  { href: "/kontakt", label: "Kontakt" },
]

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const scrollY = useScrollPosition()

  const isScrolled = scrollY > 50

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShopDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => { document.body.style.overflow = "" }
  }, [mobileMenuOpen])

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current)
    setShopDropdownOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setShopDropdownOpen(false), 150)
  }

  const closeMobile = () => {
    setMobileMenuOpen(false)
  }

  return (
    <>
      <AnnouncementBar />
      <header
        className={cn(
          "fixed left-0 right-0 z-40 transition-all duration-300",
          isScrolled
            ? "glass-header shadow-md"
            : "bg-white/95 backdrop-blur-sm shadow-sm"
        )}
        style={{ top: "var(--announcement-height, 36px)" }}
      >
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          {/* Logo */}
          <Link to="/" className="relative z-50 flex items-center">
            <img
              src={logo}
              alt="MM Queen Magnetics"
              className="h-10 w-auto"
              width={144}
              height={79}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            {/* Trgovina mega menu */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to="/trgovina"
                className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted hover:text-primary"
              >
                <ShoppingBag className="h-4 w-4" />
                Trgovina
                <ChevronDown className={cn(
                  "h-3.5 w-3.5 transition-transform duration-200",
                  shopDropdownOpen && "rotate-180"
                )} />
              </Link>

              <div
                className={cn(
                  "absolute left-1/2 top-full mt-2 w-56 -translate-x-1/2 rounded-xl border border-border bg-background p-2 shadow-lg transition-all duration-200",
                  shopDropdownOpen
                    ? "pointer-events-auto translate-y-0 opacity-100"
                    : "pointer-events-none translate-y-2 opacity-0"
                )}
              >
                {shopLinks.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-muted"
                    onClick={() => setShopDropdownOpen(false)}
                  >
                    <span className="text-sm font-medium text-foreground">
                      {item.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Cart & Mobile Menu Trigger */}
          <div className="flex items-center gap-3">
            <CartDrawer />
            <Button
              variant="ghost"
              size="icon"
              className="relative z-50 text-primary md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Zapri meni" : "Odpri meni"}
            >
              {mobileMenuOpen
                ? <X className="h-7 w-7" strokeWidth={2.5} />
                : <Menu className="h-7 w-7" strokeWidth={2.5} />}
            </Button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <Suspense fallback={null}>
          <MobileMenu open={mobileMenuOpen} onClose={closeMobile} />
        </Suspense>
      )}
    </>
  )
}

export default Header
